import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Loader2, Monitor, Smartphone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CLAIM_LANGS } from "@/lib/sales";

function Meta({ label, value, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-fundio-faint">{label}</span>
      <span data-testid={testid} className="truncate text-xs font-medium text-fundio-ink" title={value}>
        {value || <span className="italic text-fundio-faint">—</span>}
      </span>
    </div>
  );
}

export function ClaimEmailPreview({ open, onClose, orgName, recipient, claimUrl }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState("de");
  const [device, setDevice] = useState("desktop");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.post("/sales/claim-email/preview", {
      language: lang,
      org_name: orgName || "FUNDIO CLAIM QA",
      recipient_email: recipient || "",
      claim_url: claimUrl || null,
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [lang, orgName, recipient, claimUrl]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) setDevice("desktop"); }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent data-testid="claim-email-preview" className="max-w-4xl gap-0 p-0 overflow-hidden">
        <DialogHeader className="border-b border-fundio-border px-5 py-3">
          <DialogTitle className="flex items-center gap-2 font-display text-fundio-ink">
            <Mail className="h-4 w-4 text-fundio-gold" /> {t("emailPreview.title")}
            <span className="rounded border border-status-info-border bg-status-info-bg px-1.5 py-0.5 text-[10px] font-semibold text-status-info">
              {t("emailPreview.badge")}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 border-b border-fundio-border bg-fundio-subtle px-5 py-2.5">
          <div data-testid="preview-lang-tabs" className="flex gap-1">
            {CLAIM_LANGS.map(([code, label]) => (
              <button key={code} data-testid={`preview-lang-${code}`} onClick={() => setLang(code)}
                className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${lang === code
                  ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep"
                  : "border-fundio-border bg-white text-fundio-muted hover:border-fundio-gold"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <button data-testid="preview-device-desktop" onClick={() => setDevice("desktop")}
              className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold ${device === "desktop"
                ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-muted"}`}>
              <Monitor className="h-3.5 w-3.5" /> {t("emailPreview.desktop")}
            </button>
            <button data-testid="preview-device-mobile" onClick={() => setDevice("mobile")}
              className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold ${device === "mobile"
                ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep" : "border-fundio-border bg-white text-fundio-muted"}`}>
              <Smartphone className="h-3.5 w-3.5" /> {t("emailPreview.mobile")}
            </button>
          </div>
        </div>

        {data && (
          <div className="grid grid-cols-2 gap-x-5 gap-y-2 border-b border-fundio-border px-5 py-3 sm:grid-cols-3">
            <Meta label={t("emailPreview.subject")} value={data.subject} testid="preview-subject" />
            <Meta label={t("emailPreview.from")} value={data.from_name} testid="preview-from" />
            <Meta label={t("emailPreview.replyTo")} value={data.reply_to} testid="preview-reply-to" />
            <Meta label={t("emailPreview.recipient")} value={data.recipient} testid="preview-recipient" />
            <Meta label={t("emailPreview.language")} value={(data.language || "").toUpperCase()} testid="preview-language" />
            <Meta label={t("emailPreview.ctaTarget")} value={data.cta_target} testid="preview-cta-target" />
          </div>
        )}

        <div className="max-h-[52vh] overflow-y-auto bg-[#0B1220] p-5">
          {loading || !data ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
          ) : (
            <div className="mx-auto transition-all" style={{ width: device === "mobile" ? 390 : "100%", maxWidth: device === "mobile" ? 390 : 680 }}>
              <iframe data-testid="preview-iframe" title="email-preview" srcDoc={data.html}
                className="w-full rounded-lg bg-white" style={{ height: device === "mobile" ? 620 : 700, border: "none" }} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
